import { useState } from "react";
import { Bell, ChevronDown, ChevronUp, Info, RotateCcw } from "lucide-react";
import { PALETTE } from "../../styles/configStyles";

/* ═══════════════════════════════════════════════════════════════════
   DEFAULT TEMPLATES — kept in sync with notificationMessages.js
   ═══════════════════════════════════════════════════════════════════ */
const DEFAULTS = {
  leave: {
    submitted: {
      en: "Your leave request ({leaveType}) from {startDate} to {endDate} has been submitted successfully and is pending approval.",
      ar: "تم إرسال طلب الإجازة ({leaveType}) من {startDate} إلى {endDate} بنجاح وهو بانتظار الموافقة.",
    },
    submittedTitle: {
      en: "Leave Request Submitted ({leaveType})",
      ar: "تم تقديم طلب الإجازة ({leaveType})",
    },
    adminAlert: {
      en: "{employeeName} — {days} days · Reason: {reason}",
      ar: "{employeeName} — {days} يوم · السبب: {reason}",
    },
    adminAlertTitle: {
      en: "Leave Approval Required ({leaveType})",
      ar: "مطلوب موافقة على إجازة ({leaveType})",
    },
    statusChange: {
      en: "Your {leaveType} request from {startDate} to {endDate} has been {statusText}{byApprovedBy}{commentsText}",
      ar: "تم {statusText} طلب {leaveType} من {startDate} إلى {endDate}{byApprovedBy}{commentsText}",
    },
    statusChangeTitle: {
      en: "Leave Request {statusText}",
      ar: "طلب الإجازة {statusText}",
    },
  },
  advancement: {
    submitted: {
      en: "Your advancement request for {amount} has been submitted successfully and is being processed.",
      ar: "تم إرسال طلب السلفة بقيمة {amount} بنجاح وهو قيد المعالجة.",
    },
    submittedTitle: {
      en: "Advancement Request Submitted",
      ar: "تم تقديم طلب سلفة",
    },
    adminAlert: {
      en: "{employeeName} needs {amount}",
      ar: "{employeeName} يحتاج {amount}",
    },
    adminAlertTitle: {
      en: "Advancement Request — {amount}",
      ar: "طلب سلفة — {amount}",
    },
    approved: {
      en: "{managerName} approved your advancement request of {amount}.{notesText}",
      ar: "{managerName} وافق على طلب السلفة الخاص بك بقيمة {amount}.{notesText}",
    },
    approvedTitle: {
      en: "Advancement Request Approved",
      ar: "تمت الموافقة على طلب السلفة",
    },
    rejected: {
      en: "{managerName} rejected your advancement request of {amount}.{reasonText}",
      ar: "{managerName} رفض طلب السلفة الخاص بك بقيمة {amount}.{reasonText}",
    },
    rejectedTitle: {
      en: "Advancement Request Rejected",
      ar: "تم رفض طلب السلفة",
    },
    disbursed: {
      en: "{managerName} disbursed {amount} to you.{methodText}{refNumberText}",
      ar: "{managerName} صرف لك {amount}.{methodText}{refNumberText}",
    },
    disbursedTitle: {
      en: "Advancement Disbursed",
      ar: "تم صرف السلفة",
    },
    adminStatusChange: {
      en: "{employeeName} — {amount}. {managerName} changed request #{requestId} status to {status}. {employeeMessage}",
      ar: "{employeeName} — {amount}. {managerName} غيّر حالة الطلب #{requestId} إلى {status}. {employeeMessage}",
    },
    adminStatusChangeTitle: {
      en: "Advancement Request {status}",
      ar: "طلب سلفة {status}",
    },
    updated: {
      en: "{updaterName} updated advancement request #{requestId}. Requested amount: {amount}.",
      ar: "{updaterName} حدّث طلب السلفة #{requestId}. المبلغ المطلوب: {amount}.",
    },
    updatedTitle: {
      en: "Advancement Request Updated",
      ar: "تم تحديث طلب السلفة",
    },
  },
  document: {
    submitted: {
      en: "Your document request ({requestType}) has been submitted successfully and is being processed.",
      ar: "تم إرسال طلب المستند ({requestType}) بنجاح وهو قيد المعالجة.",
    },
    submittedTitle: {
      en: "Document Request Submitted",
      ar: "تم تقديم طلب مستند",
    },
    adminAlert: {
      en: "{employeeName} requested a {docType}",
      ar: "{employeeName} طلب {docType}",
    },
    adminAlertTitle: {
      en: "Document Request",
      ar: "طلب مستند",
    },
    completed: {
      en: "{employeeName} - your document request for {requestType} has been completed.",
      ar: "{employeeName} - تم إكمال طلب المستند الخاص بك ({requestType}).",
    },
    completedTitle: {
      en: "Document Request Completed",
      ar: "تم إكمال طلب المستند",
    },
    completedAdmin: {
      en: "{employeeName} - their document request for {requestType} was completed.",
      ar: "{employeeName} - تم إكمال طلب المستند ({requestType}).",
    },
    uploadApproved: {
      en: "Your uploaded {docType} document has been approved.",
      ar: "تمت الموافقة على المستند ({docType}) الذي قمت برفعه.",
    },
    uploadApprovedTitle: {
      en: "Document Upload Approved",
      ar: "تمت الموافقة على رفع المستند",
    },
    rejected: {
      en: "{employeeName} - your document request for {requestType} has been {status}.",
      ar: "{employeeName} - تم {status} طلب المستند الخاص بك ({requestType}).",
    },
    rejectedTitle: {
      en: "Document Request {statusTitle}",
      ar: "طلب مستند {statusTitle}",
    },
    adminViewed: {
      en: "{viewerName} viewed your document request for {requestType}",
      ar: "{viewerName} شاهد طلب المستند الخاص بك ({requestType})",
    },
    adminViewedTitle: {
      en: "Document Request Viewed",
      ar: "تمت مشاهدة طلب المستند",
    },
  },
  chat: {
    newMessage: {
      en: "New message from {senderName}",
      ar: "رسالة جديدة من {senderName}",
    },
    newMessageTitle: {
      en: "New Message",
      ar: "رسالة جديدة",
    },
  },
};

/* Variables available per category */
const VARIABLES = {
  leave: [
    { key: "{leaveType}", en: "Leave type", ar: "نوع الإجازة" },
    { key: "{startDate}", en: "Start date", ar: "تاريخ البداية" },
    { key: "{endDate}", en: "End date", ar: "تاريخ النهاية" },
    { key: "{employeeName}", en: "Employee name", ar: "اسم الموظف" },
    { key: "{days}", en: "Number of days", ar: "عدد الأيام" },
    { key: "{reason}", en: "Reason", ar: "السبب" },
    { key: "{statusText}", en: "Status text (approved/rejected)", ar: "الحالة (موافق عليها/مرفوضة)" },
    { key: "{approvedBy}", en: "Approver Name (Manager)", ar: "اسم المدير (بدون كلمة بواسطة)" },
    { key: "{byApprovedBy}", en: "Approved/Rejected by (with 'by' word)", ar: "اسم المدير (مع كلمة بواسطة)" },
    { key: "{commentsText}", en: "Comments (optional)", ar: "ملاحظات (اختياري)" },
  ],
  advancement: [
    { key: "{amount}", en: "Amount", ar: "المبلغ" },
    { key: "{employeeName}", en: "Employee name", ar: "اسم الموظف" },
    { key: "{managerName}", en: "Manager name", ar: "اسم المدير" },
    { key: "{notesText}", en: "Notes (optional)", ar: "ملاحظات (اختياري)" },
    { key: "{reasonText}", en: "Reason (optional)", ar: "السبب (اختياري)" },
    { key: "{methodText}", en: "Method (optional)", ar: "الطريقة (اختياري)" },
    { key: "{refNumberText}", en: "Ref Number (optional)", ar: "رقم المرجع (اختياري)" },
    { key: "{requestId}", en: "Request ID", ar: "رقم الطلب" },
    { key: "{status}", en: "Status", ar: "الحالة" },
    { key: "{employeeMessage}", en: "Employee message", ar: "رسالة الموظف" },
    { key: "{updaterName}", en: "Updater name", ar: "اسم المحدّث" },
  ],
  document: [
    { key: "{requestType}", en: "Document type", ar: "نوع المستند" },
    { key: "{docType}", en: "Document type", ar: "نوع المستند" },
    { key: "{employeeName}", en: "Employee name", ar: "اسم الموظف" },
    { key: "{status}", en: "Status", ar: "الحالة" },
    { key: "{statusTitle}", en: "Status (Title Cased)", ar: "الحالة (كعنوان)" },
    { key: "{viewerName}", en: "Viewer name", ar: "اسم المشاهد" },
  ],
  chat: [
    { key: "{senderName}", en: "Sender name", ar: "اسم المرسل" },
  ],
};

/* ───────────────────────────────────────────────────────────────── */

const NotificationsTab = ({
  styles,
  t,
  theme,
  isRTL,
  language,
  config,
  setConfig,
  canEditField,
  getFieldStyle,
}) => {
  const canEdit = canEditField("leaves"); // re-use leaves permission scope
  const [expandedCategory, setExpandedCategory] = useState("leave");
  const [expandedTemplate, setExpandedTemplate] = useState(null);

  const nt = t.notifications || {};

  // Read templates from config (or fall back to DEFAULTS)
  const templates = config.notificationTemplates || {};

  const getTemplate = (category, key, lang) => {
    return templates?.[category]?.[key]?.[lang]
      ?? DEFAULTS[category]?.[key]?.[lang]
      ?? "";
  };

  const setTemplate = (category, key, lang, value) => {
    if (!canEdit) return;
    setConfig((prev) => {
      const existing = prev.notificationTemplates || {};
      return {
        ...prev,
        notificationTemplates: {
          ...existing,
          [category]: {
            ...(existing[category] || {}),
            [key]: {
              ...(existing[category]?.[key] || DEFAULTS[category]?.[key] || {}),
              [lang]: value,
            },
          },
        },
      };
    });
  };

  const resetTemplate = (category, key) => {
    if (!canEdit) return;
    setConfig((prev) => {
      const existing = { ...(prev.notificationTemplates || {}) };
      if (existing[category]) {
        const cat = { ...existing[category] };
        delete cat[key];
        existing[category] = cat;
      }
      return { ...prev, notificationTemplates: existing };
    });
  };

  /* ── Category config ── */
  const categories = [
    { id: "leave",       label: nt.leaveCategory       || "Leave Notifications",       labelAr: "إشعارات الإجازات" },
    { id: "advancement", label: nt.advancementCategory  || "Advancement Notifications", labelAr: "إشعارات السلف" },
    { id: "document",    label: nt.documentCategory     || "Document Notifications",    labelAr: "إشعارات المستندات" },
    { id: "chat",        label: nt.chatCategory         || "Chat Notifications",        labelAr: "إشعارات الدردشة" },
  ];

  const templateLabels = {
    // Leave
    submitted:              { en: "Leave Request Submitted Message", ar: "رسالة إرسال طلب الإجازة (تأكيد للموظف)" },
    submittedTitle:         { en: "Leave Request Submitted Title",   ar: "عنوان إرسال طلب الإجازة" },
    adminAlert:             { en: "Manager Leave Request Alert Message", ar: "رسالة تنبيه المدير بطلب الإجازة" },
    adminAlertTitle:        { en: "Manager Leave Request Alert Title",   ar: "عنوان تنبيه المدير بطلب الإجازة" },
    statusChange:           { en: "Leave Status Change Message",         ar: "رسالة الموافقة/الرفض على الإجازة" },
    statusChangeTitle:      { en: "Leave Status Change Title",           ar: "عنوان الموافقة/الرفض على الإجازة" },
    // Advancement
    approved:               { en: "Approved Message",                ar: "رسالة الموافقة" },
    approvedTitle:          { en: "Approved Title",                  ar: "عنوان الموافقة" },
    rejected:               { en: "Rejected Message",                ar: "رسالة الرفض" },
    rejectedTitle:          { en: "Rejected Title",                  ar: "عنوان الرفض" },
    disbursed:              { en: "Disbursed Message",               ar: "رسالة الصرف" },
    disbursedTitle:         { en: "Disbursed Title",                 ar: "عنوان الصرف" },
    adminStatusChange:      { en: "Admin Status Change Message",     ar: "رسالة الإدارة لتغيير الحالة" },
    adminStatusChangeTitle: { en: "Admin Status Change Title",       ar: "عنوان الإدارة لتغيير الحالة" },
    updated:                { en: "Updated Message",                 ar: "رسالة التحديث" },
    updatedTitle:           { en: "Updated Title",                   ar: "عنوان التحديث" },
    // Document
    completed:              { en: "Completed Message",               ar: "رسالة الإكمال" },
    completedTitle:         { en: "Completed Title",                 ar: "عنوان الإكمال" },
    completedAdmin:         { en: "Admin Completed Message",         ar: "رسالة إكمال الإدارة" },
    uploadApproved:         { en: "Upload Approved Message",         ar: "رسالة الموافقة على الرفع" },
    uploadApprovedTitle:    { en: "Upload Approved Title",           ar: "عنوان الموافقة على الرفع" },
    adminViewed:            { en: "Admin Viewed Message",            ar: "رسالة مشاهدة الإدارة" },
    adminViewedTitle:       { en: "Admin Viewed Title",              ar: "عنوان مشاهدة الإدارة" },
    // Chat
    newMessage:             { en: "New Message Alert",               ar: "تنبيه رسالة جديدة" },
    newMessageTitle:        { en: "New Message Title",               ar: "عنوان رسالة جديدة" },
  };

  /* ── Styles ── */
  const isDark = theme === "dark";
  const cardBg = isDark ? "rgba(255,255,255,0.04)" : "rgba(249,250,251,0.8)";
  const cardBorder = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";
  const textPrimary = isDark ? "#f1f5f9" : "#0f172a";
  const textSecondary = isDark ? "rgba(226,232,240,0.7)" : "#64748b";

  const textareaStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "12px 14px",
    minHeight: "80px",
    background: isDark ? "rgba(255,255,255,0.06)" : "#fff",
    border: isDark ? "1.5px solid rgba(255,255,255,0.12)" : "1.5px solid rgba(0,0,0,0.1)",
    borderRadius: "10px",
    color: textPrimary,
    fontSize: "13px",
    fontFamily: "'Inter', sans-serif",
    lineHeight: "1.6",
    resize: "vertical",
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
    direction: "ltr", // templates are always LTR-friendly for variables
  };

  const inputStyle = {
    ...textareaStyle,
    minHeight: "42px",
    resize: "none",
  };

  return (
    <div style={{ marginTop: "24px", marginBottom: "24px" }}>
      {/* Section header */}
      <h3 style={styles.sectionTitle}>
        <div style={styles.sectionIconBadge(PALETTE.primary.base)}>
          <Bell size={20} />
        </div>
        {isRTL ? "قوالب الإشعارات" : "Notification Templates"}
        {!canEdit && (
          <span style={{
            fontSize: "11px", fontWeight: "700",
            marginLeft: isRTL ? "0" : "12px",
            marginRight: isRTL ? "12px" : "0",
            padding: "4px 12px", borderRadius: "6px",
            background: `${PALETTE.amber.base}15`,
            color: PALETTE.amber.base,
            border: `1px solid ${PALETTE.amber.base}25`,
          }}>
            {isRTL ? "(للقراءة فقط)" : "(Read-only)"}
          </span>
        )}
      </h3>

      <p style={{
        color: textSecondary,
        fontSize: "13px",
        marginBottom: "20px",
        lineHeight: "1.6",
        textAlign: isRTL ? "right" : "left",
      }}>
        {isRTL
          ? "قم بتخصيص نصوص الإشعارات المرسلة للموظفين والإدارة. استخدم المتغيرات بين الأقواس مثل {employeeName} لإدراج بيانات ديناميكية."
          : "Customize the notification messages sent to employees and admins. Use variables in curly braces like {employeeName} to insert dynamic data."}
      </p>

      {/* ── Category Accordion ── */}
      {categories.map((cat) => {
        const isExpanded = expandedCategory === cat.id;
        const catTemplateKeys = Object.keys(DEFAULTS[cat.id] || {});
        const vars = VARIABLES[cat.id] || [];

        return (
          <div
            key={cat.id}
            style={{
              marginBottom: "12px",
              borderRadius: "14px",
              border: `1px solid ${isExpanded ? PALETTE.primary.base + "40" : cardBorder}`,
              background: cardBg,
              overflow: "hidden",
              transition: "all 0.3s ease",
            }}
          >
            {/* Category header */}
            <button
              onClick={() => setExpandedCategory(isExpanded ? null : cat.id)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 20px",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: textPrimary,
                fontSize: "15px",
                fontWeight: "700",
                direction: isRTL ? "rtl" : "ltr",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Bell size={18} color={PALETTE.primary.base} />
                {isRTL ? cat.labelAr : cat.label}
                <span style={{
                  fontSize: "11px", fontWeight: "600",
                  padding: "2px 8px", borderRadius: "6px",
                  background: isDark ? "rgba(99,102,241,0.15)" : "rgba(99,102,241,0.1)",
                  color: PALETTE.primary.base,
                }}>
                  {catTemplateKeys.length} {isRTL ? "قالب" : "templates"}
                </span>
              </span>
              {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>

            {/* Expanded content */}
            {isExpanded && (
              <div style={{ padding: "0 20px 20px" }}>
                {/* Variable chips */}
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "6px",
                  marginBottom: "16px",
                  padding: "10px 14px",
                  borderRadius: "10px",
                  background: isDark ? "rgba(99,102,241,0.08)" : "rgba(99,102,241,0.05)",
                  border: `1px solid ${isDark ? "rgba(99,102,241,0.15)" : "rgba(99,102,241,0.1)"}`,
                }}>
                  <Info size={14} color={PALETTE.primary.base} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: "12px", fontWeight: "600", color: textSecondary, marginRight: "6px" }}>
                    {isRTL ? "المتغيرات المتاحة:" : "Available variables:"}
                  </span>
                  {vars.map((v) => (
                    <span
                      key={v.key}
                      title={isRTL ? v.ar : v.en}
                      style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontWeight: "600",
                        fontFamily: "monospace",
                        background: isDark ? "rgba(255,255,255,0.08)" : "#fff",
                        border: `1px solid ${cardBorder}`,
                        color: PALETTE.primary.base,
                        cursor: "help",
                      }}
                    >
                      {v.key}
                    </span>
                  ))}
                </div>

                {/* Template editors */}
                {catTemplateKeys.map((tplKey) => {
                  const tplLabel = templateLabels[tplKey] || { en: tplKey, ar: tplKey };
                  const isTitle = tplKey.includes("Title");
                  const isTplExpanded = expandedTemplate === `${cat.id}.${tplKey}`;
                  const enVal = getTemplate(cat.id, tplKey, "en");
                  const arVal = getTemplate(cat.id, tplKey, "ar");
                  const isDefault = !templates?.[cat.id]?.[tplKey];

                  return (
                    <div
                      key={tplKey}
                      style={{
                        marginBottom: "10px",
                        borderRadius: "10px",
                        border: `1px solid ${cardBorder}`,
                        background: isDark ? "rgba(255,255,255,0.02)" : "#fff",
                        overflow: "hidden",
                      }}
                    >
                      {/* Template row header */}
                      <button
                        onClick={() => setExpandedTemplate(isTplExpanded ? null : `${cat.id}.${tplKey}`)}
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "12px 16px",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: textPrimary,
                          fontSize: "13px",
                          fontWeight: "600",
                          direction: isRTL ? "rtl" : "ltr",
                        }}
                      >
                        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          {isRTL ? tplLabel.ar : tplLabel.en}
                          {isDefault && (
                            <span style={{
                              fontSize: "10px", fontWeight: "600",
                              padding: "1px 6px", borderRadius: "4px",
                              background: isDark ? "rgba(16,185,129,0.15)" : "rgba(16,185,129,0.1)",
                              color: "#10b981",
                            }}>
                              {isRTL ? "افتراضي" : "Default"}
                            </span>
                          )}
                        </span>
                        {isTplExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>

                      {/* Template editing fields */}
                      {isTplExpanded && (
                        <div style={{ padding: "0 16px 16px" }}>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))", gap: "12px" }}>
                            {/* English */}
                            <div>
                              <label style={{
                                display: "block",
                                fontSize: "11px",
                                fontWeight: "700",
                                color: textSecondary,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                marginBottom: "6px",
                              }}>
                                🇬🇧 English
                              </label>
                              {isTitle ? (
                                <input
                                  type="text"
                                  value={enVal}
                                  onChange={(e) => setTemplate(cat.id, tplKey, "en", e.target.value)}
                                  disabled={!canEdit}
                                  style={{
                                    ...inputStyle,
                                    opacity: canEdit ? 1 : 0.6,
                                    cursor: canEdit ? "text" : "not-allowed",
                                  }}
                                />
                              ) : (
                                <textarea
                                  value={enVal}
                                  onChange={(e) => setTemplate(cat.id, tplKey, "en", e.target.value)}
                                  disabled={!canEdit}
                                  style={{
                                    ...textareaStyle,
                                    opacity: canEdit ? 1 : 0.6,
                                    cursor: canEdit ? "text" : "not-allowed",
                                  }}
                                />
                              )}
                            </div>

                            {/* Arabic */}
                            <div>
                              <label style={{
                                display: "block",
                                fontSize: "11px",
                                fontWeight: "700",
                                color: textSecondary,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                marginBottom: "6px",
                              }}>
                                🇸🇦 العربية
                              </label>
                              {isTitle ? (
                                <input
                                  type="text"
                                  value={arVal}
                                  onChange={(e) => setTemplate(cat.id, tplKey, "ar", e.target.value)}
                                  disabled={!canEdit}
                                  dir="rtl"
                                  style={{
                                    ...inputStyle,
                                    direction: "rtl",
                                    opacity: canEdit ? 1 : 0.6,
                                    cursor: canEdit ? "text" : "not-allowed",
                                  }}
                                />
                              ) : (
                                <textarea
                                  value={arVal}
                                  onChange={(e) => setTemplate(cat.id, tplKey, "ar", e.target.value)}
                                  disabled={!canEdit}
                                  dir="rtl"
                                  style={{
                                    ...textareaStyle,
                                    direction: "rtl",
                                    opacity: canEdit ? 1 : 0.6,
                                    cursor: canEdit ? "text" : "not-allowed",
                                  }}
                                />
                              )}
                            </div>
                          </div>

                          {/* Reset button */}
                          {!isDefault && canEdit && (
                            <button
                              onClick={() => resetTemplate(cat.id, tplKey)}
                              style={{
                                marginTop: "10px",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "6px 12px",
                                borderRadius: "8px",
                                border: `1px solid ${isDark ? "rgba(239,68,68,0.3)" : "rgba(239,68,68,0.2)"}`,
                                background: "none",
                                color: "#ef4444",
                                fontSize: "12px",
                                fontWeight: "600",
                                cursor: "pointer",
                                transition: "all 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "rgba(239,68,68,0.08)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "none";
                              }}
                            >
                              <RotateCcw size={12} />
                              {isRTL ? "إعادة للافتراضي" : "Reset to default"}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default NotificationsTab;
